import { useRolodexEdit } from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useState } from 'react'
import {
  GeckoComponent,
  GeckoComponentDeficiency,
  GeckoSite,
  GeckoUnit,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

interface UseDeficienciesWidgetProps {
  unit: GeckoUnit | undefined
  site: GeckoSite | undefined
  riskAssessmentId: string
  onDeficiencyCreated?: (deficiencyId: string) => void
  deficiencies: readonly GeckoComponentDeficiency[]
  components: readonly GeckoComponent[]
  forceRefresh: () => Promise<void>
}

export const useDeficienciesWidget = ({
  unit,
  site,
  riskAssessmentId,
  onDeficiencyCreated,
  forceRefresh,
}: UseDeficienciesWidgetProps) => {
  const [selectedDeficiencyId, setSelectedDeficiencyId] = useState<
    string | undefined
  >()

  const { permissions } = useGeckoPermissions()
  const { rolodexPut, rolodexPatch, rolodexDelete } = useRolodexEdit()

  const canEdit =
    permissions?.has(PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR) ?? false
  const canView =
    permissions?.has(PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER) ||
    permissions?.has(PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR) ||
    permissions?.has(PERMISSION_GECKO_INTERNAL_MEMBER) ||
    false

  const createDeficiency = useCallback(
    async (deficiencyData: {
      title: string
      description?: string
      componentId?: string
    }) => {
      if (!canEdit) {
        return
      }
      if (!unit || !site) {
        return
      }

      try {
        const deficiencyEntry = {
          id: uuidv4(),
          type: RolodexEntryType.COMPONENT_DEFICIENCY,
          data: {
            title: deficiencyData.title,
            description: deficiencyData.description,
          },
          tags: {
            organization: site.tags.organization,
            site: site.id,
            unit: unit.id,
            [RolodexEntryType.RISK_ASSESSMENT]: riskAssessmentId,
            ...(deficiencyData.componentId && {
              component: deficiencyData.componentId,
            }),
          },
          index: {},
        }

        await rolodexPut([deficiencyEntry])
        await forceRefresh()

        onDeficiencyCreated?.(deficiencyEntry.id)
      } catch (error) {
        console.error('Failed to create deficiency:', error)
      }
    },
    [
      canEdit,
      site,
      unit,
      riskAssessmentId,
      rolodexPut,
      forceRefresh,
      onDeficiencyCreated,
    ]
  )

  const editDeficiency = useCallback(
    async (
      deficiencyId: string,
      updates: Partial<GeckoComponentDeficiency['data']>
    ) => {
      if (!canEdit) {
        return
      }

      try {
        await rolodexPatch([
          {
            id: deficiencyId,
            data: updates,
          },
        ])
        await forceRefresh()
      } catch (error) {
        console.error('Failed to update deficiency:', error)
      }
    },
    [canEdit, rolodexPatch, forceRefresh]
  )

  const deleteDeficiency = useCallback(
    async (deficiencyId: string) => {
      if (!canEdit) {
        return
      }

      try {
        await rolodexDelete(deficiencyId, false)
        await forceRefresh()
        setSelectedDeficiencyId((prev) =>
          prev === deficiencyId ? undefined : prev
        )
      } catch (error) {
        console.error('Failed to delete deficiency:', error)
      }
    },
    [canEdit, forceRefresh, rolodexDelete]
  )

  return {
    canEdit,
    canView,
    selectedDeficiencyId,
    setSelectedDeficiencyId,
    createDeficiency,
    editDeficiency,
    deleteDeficiency,
  }
}
